/**
 * ルート定義（03 6.2）。社内のルートは RequireAuth の下に、公開のルート（/login・/f/:form_code）はその外に置く。
 * SCR-057（/f/:form_code）は社内のシェル・認証（AuthSessionProvider）の外に置き、public-form の別チャンクを遅延読み込みする（03 8 章）。
 * 画面は React.lazy で遅延読み込みする（03 3.2.1）。まだ作っていない画面は PreparingPage（準備中）。
 */
import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { PageLoading } from '../../components/feedback/PageLoading';
import { AuthSessionProvider } from '../../features/auth/components/AuthSessionProvider';
import { AppShell } from '../shell/AppShell';
import { RequireAuth } from './RequireAuth';
import { RequirePermission } from './RequirePermission';
import { REDIRECT_SCREENS, SHELL_SCREENS } from './screen-definitions';

const LoginPage = lazy(() => import('../../pages/auth/LoginPage').then((module) => ({ default: module.LoginPage })));
const NotFoundPage = lazy(() =>
  import('../../pages/errors/NotFoundPage').then((module) => ({ default: module.NotFoundPage })),
);
const PreparingPage = lazy(() =>
  import('../../pages/placeholder/PreparingPage').then((module) => ({ default: module.PreparingPage })),
);
const DashboardPage = lazy(() =>
  import('../../pages/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage })),
);
const CompanyDetailPage = lazy(() =>
  import('../../pages/companies/CompanyDetailPage').then((module) => ({ default: module.CompanyDetailPage })),
);
const CompanyFormPage = lazy(() =>
  import('../../pages/companies/CompanyFormPage').then((module) => ({ default: module.CompanyFormPage })),
);
const CustomerListPage = lazy(() =>
  import('../../pages/customers/CustomerListPage').then((module) => ({ default: module.CustomerListPage })),
);
const CustomerCreatePage = lazy(() =>
  import('../../pages/customers/CustomerCreatePage').then((module) => ({ default: module.CustomerCreatePage })),
);
const SoleProprietorDetailPage = lazy(() =>
  import('../../pages/sole-proprietors/SoleProprietorDetailPage').then((module) => ({
    default: module.SoleProprietorDetailPage,
  })),
);
const SoleProprietorFormPage = lazy(() =>
  import('../../pages/sole-proprietors/SoleProprietorFormPage').then((module) => ({
    default: module.SoleProprietorFormPage,
  })),
);
const TagListPage = lazy(() =>
  import('../../pages/tags/TagListPage').then((module) => ({ default: module.TagListPage })),
);

const CaseListPage = lazy(() =>
  import('../../pages/cases/CaseListPage').then((module) => ({ default: module.CaseListPage })),
);
const CaseDetailPage = lazy(() =>
  import('../../pages/cases/CaseDetailPage').then((module) => ({ default: module.CaseDetailPage })),
);
const CaseFormPage = lazy(() =>
  import('../../pages/cases/CaseFormPage').then((module) => ({ default: module.CaseFormPage })),
);
const TransactionListPage = lazy(() =>
  import('../../pages/transactions/TransactionListPage').then((module) => ({ default: module.TransactionListPage })),
);
const TransactionDetailPage = lazy(() =>
  import('../../pages/transactions/TransactionDetailPage').then((module) => ({
    default: module.TransactionDetailPage,
  })),
);
const TransactionFormPage = lazy(() =>
  import('../../pages/transactions/TransactionFormPage').then((module) => ({ default: module.TransactionFormPage })),
);
const ActivityListPage = lazy(() =>
  import('../../pages/activities/ActivityListPage').then((module) => ({ default: module.ActivityListPage })),
);
const ActivityDetailPage = lazy(() =>
  import('../../pages/activities/ActivityDetailPage').then((module) => ({ default: module.ActivityDetailPage })),
);
const CalendarPage = lazy(() =>
  import('../../pages/calendar/CalendarPage').then((module) => ({ default: module.CalendarPage })),
);
const GoalListPage = lazy(() =>
  import('../../pages/goals/GoalListPage').then((module) => ({ default: module.GoalListPage })),
);
const GoalDetailPage = lazy(() =>
  import('../../pages/goals/GoalDetailPage').then((module) => ({ default: module.GoalDetailPage })),
);
const GoalFormPage = lazy(() =>
  import('../../pages/goals/GoalFormPage').then((module) => ({ default: module.GoalFormPage })),
);
const ReportPage = lazy(() =>
  import('../../pages/reports/ReportPage').then((module) => ({ default: module.ReportPage })),
);
const ReportBuilderPage = lazy(() =>
  import('../../pages/reports/ReportBuilderPage').then((module) => ({ default: module.ReportBuilderPage })),
);
const DailyReportPage = lazy(() =>
  import('../../pages/reports/DailyReportPage').then((module) => ({ default: module.DailyReportPage })),
);
const ReportTemplateListPage = lazy(() =>
  import('../../pages/reports/ReportTemplateListPage').then((module) => ({ default: module.ReportTemplateListPage })),
);
const UserPerformancePage = lazy(() =>
  import('../../pages/reports/UserPerformancePage').then((module) => ({ default: module.UserPerformancePage })),
);

const PersonnelListPage = lazy(() =>
  import('../../pages/personnel/PersonnelListPage').then((module) => ({ default: module.PersonnelListPage })),
);
const PersonnelDetailPage = lazy(() =>
  import('../../pages/personnel/PersonnelDetailPage').then((module) => ({ default: module.PersonnelDetailPage })),
);
const PersonnelFormPage = lazy(() =>
  import('../../pages/personnel/PersonnelFormPage').then((module) => ({ default: module.PersonnelFormPage })),
);
const ContractListPage = lazy(() =>
  import('../../pages/contracts/ContractListPage').then((module) => ({ default: module.ContractListPage })),
);
const ContractDetailPage = lazy(() =>
  import('../../pages/contracts/ContractDetailPage').then((module) => ({ default: module.ContractDetailPage })),
);
const ContractFormPage = lazy(() =>
  import('../../pages/contracts/ContractFormPage').then((module) => ({ default: module.ContractFormPage })),
);
const PriceChangePage = lazy(() =>
  import('../../pages/contracts/PriceChangePage').then((module) => ({ default: module.PriceChangePage })),
);
const ChecklistTemplateListPage = lazy(() =>
  import('../../pages/checklists/ChecklistTemplateListPage').then((module) => ({
    default: module.ChecklistTemplateListPage,
  })),
);
const ChecklistTemplateEditPage = lazy(() =>
  import('../../pages/checklists/ChecklistTemplateEditPage').then((module) => ({
    default: module.ChecklistTemplateEditPage,
  })),
);
const MonthlyChecklistListPage = lazy(() =>
  import('../../pages/checklists/MonthlyChecklistListPage').then((module) => ({
    default: module.MonthlyChecklistListPage,
  })),
);
const ChecklistMatrixPage = lazy(() =>
  import('../../pages/checklists/ChecklistMatrixPage').then((module) => ({ default: module.ChecklistMatrixPage })),
);
const ChecklistDetailPage = lazy(() =>
  import('../../pages/checklists/ChecklistDetailPage').then((module) => ({ default: module.ChecklistDetailPage })),
);

const ContactPage = lazy(() =>
  import('../../pages/contacts/ContactPage').then((module) => ({ default: module.ContactPage })),
);
const FormListPage = lazy(() =>
  import('../../pages/forms/FormListPage').then((module) => ({ default: module.FormListPage })),
);
const FormDetailPage = lazy(() =>
  import('../../pages/forms/FormDetailPage').then((module) => ({ default: module.FormDetailPage })),
);
const FormBuilderPage = lazy(() =>
  import('../../pages/forms/FormBuilderPage').then((module) => ({ default: module.FormBuilderPage })),
);
const FormSubmissionListPage = lazy(() =>
  import('../../pages/form-submissions/FormSubmissionListPage').then((module) => ({
    default: module.FormSubmissionListPage,
  })),
);
const FormSubmissionDetailPage = lazy(() =>
  import('../../pages/form-submissions/FormSubmissionDetailPage').then((module) => ({
    default: module.FormSubmissionDetailPage,
  })),
);
/** SCR-057 公開フォーム（社内の画面と別のチャンク） */
const PublicFormPage = lazy(() =>
  import('../../public-form/pages/PublicFormPage').then((module) => ({ default: module.PublicFormPage })),
);
// ⑦：出力・取込・監査・マイページ・設定・利用者／ロール
const CustomerExportPage = lazy(() =>
  import('../../pages/customers/CustomerExportPage').then((module) => ({ default: module.CustomerExportPage })),
);
const CustomerImportPage = lazy(() =>
  import('../../pages/customers/CustomerImportPage').then((module) => ({ default: module.CustomerImportPage })),
);
const ChecklistImportPage = lazy(() =>
  import('../../pages/checklists/ChecklistImportPage').then((module) => ({ default: module.ChecklistImportPage })),
);
const ContractImportPage = lazy(() =>
  import('../../pages/contracts/ContractImportPage').then((module) => ({ default: module.ContractImportPage })),
);
const PersonnelImportPage = lazy(() =>
  import('../../pages/personnel/PersonnelImportPage').then((module) => ({ default: module.PersonnelImportPage })),
);
const AuditLogPage = lazy(() =>
  import('../../pages/audit-logs/AuditLogPage').then((module) => ({ default: module.AuditLogPage })),
);
const MyPage = lazy(() => import('../../pages/mypage/MyPage').then((module) => ({ default: module.MyPage })));
const CompanySettingsPage = lazy(() =>
  import('../../pages/settings/CompanySettingsPage').then((module) => ({ default: module.CompanySettingsPage })),
);
const NumberingRulePage = lazy(() =>
  import('../../pages/settings/NumberingRulePage').then((module) => ({ default: module.NumberingRulePage })),
);
const RoleListPage = lazy(() =>
  import('../../pages/settings/RoleListPage').then((module) => ({ default: module.RoleListPage })),
);
const RoleFormPage = lazy(() =>
  import('../../pages/settings/RoleFormPage').then((module) => ({ default: module.RoleFormPage })),
);
const UserListPage = lazy(() =>
  import('../../pages/users/UserListPage').then((module) => ({ default: module.UserListPage })),
);
const UserFormPage = lazy(() =>
  import('../../pages/users/UserFormPage').then((module) => ({ default: module.UserFormPage })),
);

/** 作った画面（画面 ID → 部品）。ここに無い画面は PreparingPage（N-13） */
const SCREEN_PAGES: Readonly<Record<string, () => ReactNode>> = {
  'SCR-005': () => <DashboardPage />,
  'SCR-007': () => <CompanyDetailPage />,
  'SCR-008': () => <CompanyFormPage mode="edit" />,
  'SCR-009': () => <CompanyFormPage mode="new" />,
  'SCR-010': () => <CustomerListPage />,
  'SCR-013': () => <CustomerCreatePage />,
  'SCR-015': () => <SoleProprietorDetailPage />,
  'SCR-016': () => <SoleProprietorFormPage mode="edit" />,
  'SCR-017': () => <SoleProprietorFormPage mode="new" />,
  'SCR-018': () => <ActivityListPage />,
  'SCR-019': () => <ActivityDetailPage />,
  'SCR-020': () => <CalendarPage />,
  'SCR-021': () => <CaseListPage />,
  'SCR-022': () => <CaseDetailPage />,
  'SCR-023': () => <CaseFormPage mode="edit" />,
  'SCR-024': () => <CaseFormPage mode="new" />,
  'SCR-025': () => <GoalListPage />,
  'SCR-026': () => <GoalDetailPage />,
  'SCR-027': () => <GoalFormPage mode="edit" />,
  'SCR-028': () => <GoalFormPage mode="new" />,
  'SCR-029': () => <ReportPage />,
  'SCR-030': () => <ReportBuilderPage mode="new" />,
  'SCR-031': () => <ReportBuilderPage mode="edit" />,
  'SCR-032': () => <DailyReportPage />,
  'SCR-033': () => <ReportTemplateListPage />,
  'SCR-034': () => <UserPerformancePage />,
  'SCR-035': () => <TransactionListPage />,
  'SCR-036': () => <TransactionDetailPage />,
  'SCR-037': () => <TransactionFormPage mode="edit" />,
  'SCR-038': () => <TransactionFormPage mode="new" />,
  'SCR-039': () => <ChecklistTemplateListPage />,
  'SCR-041': () => <MonthlyChecklistListPage />,
  'SCR-042': () => <ChecklistMatrixPage />,
  'SCR-043': () => <ChecklistDetailPage />,
  'SCR-045': () => <ContractListPage />,
  'SCR-046': () => <ContractDetailPage />,
  'SCR-047': () => <ContractFormPage mode="edit" />,
  'SCR-048': () => <PriceChangePage />,
  'SCR-050': () => <ContractFormPage mode="new" />,
  'SCR-051': () => <PersonnelListPage />,
  'SCR-052': () => <PersonnelDetailPage />,
  'SCR-053': () => <PersonnelFormPage mode="edit" />,
  'SCR-055': () => <PersonnelFormPage mode="new" />,
  'SCR-056': () => <ContactPage />,
  'SCR-058': () => <FormListPage />,
  'SCR-059': () => <FormDetailPage />,
  'SCR-060': () => <FormBuilderPage mode="edit" />,
  'SCR-062': () => <FormBuilderPage mode="new" />,
  'SCR-063': () => <FormSubmissionListPage />,
  'SCR-064': () => <FormSubmissionDetailPage />,
  'SCR-078': () => <TagListPage />,
  // ⑦
  'SCR-011': () => <CustomerExportPage />,
  'SCR-012': () => <CustomerImportPage />,
  'SCR-044': () => <ChecklistImportPage />,
  'SCR-049': () => <ContractImportPage />,
  'SCR-054': () => <PersonnelImportPage />,
  'SCR-065': () => <AuditLogPage />,
  'SCR-066': () => <MyPage />,
  'SCR-067': () => <CompanySettingsPage />,
  'SCR-068': () => <NumberingRulePage />,
  'SCR-069': () => <RoleListPage />,
  'SCR-070': () => <RoleFormPage mode="edit" />,
  'SCR-071': () => <RoleFormPage mode="new" />,
  'SCR-072': () => <UserListPage />,
  'SCR-073': () => <UserFormPage mode="edit" />,
  'SCR-074': () => <UserFormPage mode="new" />,
};

/** 同じ画面 ID で新規と編集のルートが分かれている画面（SCR-040：新規は /checklist-templates/new：FE-D-095） */
const PATH_PAGES: Readonly<Record<string, () => ReactNode>> = {
  '/checklist-templates/new': () => <ChecklistTemplateEditPage mode="new" />,
  '/checklist-templates/:checklist_template_code/edit': () => <ChecklistTemplateEditPage mode="edit" />,
};

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

/** すべての画面の外側（延長・再ログイン・ログアウトの仕組み） */
function RootLayout() {
  return (
    <AuthSessionProvider>
      <Outlet />
    </AuthSessionProvider>
  );
}

export function buildRoutes(): RouteObject[] {
  const shell_children: RouteObject[] = [
    ...REDIRECT_SCREENS.map((screen) => ({ path: screen.path, element: <Navigate to={screen.to} replace /> })),
    ...SHELL_SCREENS.map((screen) => {
      const renderPage = PATH_PAGES[screen.path] ?? SCREEN_PAGES[screen.screen_id];
      const page = (
        <Lazy>
          {renderPage ? (
            renderPage()
          ) : (
            <PreparingPage screen_id={screen.screen_id} title={screen.title} phase={screen.phase} />
          )}
        </Lazy>
      );
      const guarded =
        screen.allOf || screen.anyOf ? (
          <RequirePermission
            {...(screen.allOf ? { allOf: screen.allOf } : {})}
            {...(screen.anyOf ? { anyOf: screen.anyOf } : {})}
          >
            {page}
          </RequirePermission>
        ) : (
          page
        );
      return screen.path === '/' ? { index: true, element: guarded } : { path: screen.path, element: guarded };
    }),
  ];
  return [
    // SCR-057（公開・シェル無し。延長・再ログインの仕組み（RootLayout）の外）
    {
      path: '/f/:form_code',
      element: (
        <Lazy>
          <PublicFormPage />
        </Lazy>
      ),
    },
    {
      element: <RootLayout />,
      children: [
        // SCR-002（公開。ログイン済みなら / へ）
        {
          path: '/login',
          element: (
            <Lazy>
              <LoginPage />
            </Lazy>
          ),
        },
        {
          path: '/',
          element: (
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          ),
          children: shell_children,
        },
        // SCR-075（ログイン中はシェルの中に出す）
        {
          path: '*',
          element: (
            <Lazy>
              <NotFoundPage />
            </Lazy>
          ),
        },
      ],
    },
  ];
}
